/**
 * Audit Log Center (#/audit-log) — layout only.
 * Visuals inherit the command-center glass system (glass-panel + --cc-* tokens
 * from 22-overview-command-center.css). This module only adds the page grid,
 * the timeline rows, the analytics widgets, and the bottom strip.
 */

export const AUDIT_LOG_STYLE_ID = "croc-audit-log-center-v1";

const AUDIT_LOG_STYLE_TEXT = `
body[data-overview-cc="1"] .croc-cc-page-content .alog-page {
  width: 100%;
  max-width: none;
  color: var(--cc-text);
}
.alog-page {
  display: flex;
  flex-direction: column;
  gap: var(--cc-gap, 16px);
  width: 100%;
  color: var(--cc-text);
}
.alog-grid {
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr) 320px;
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "side topbar topbar"
    "side center rightcol"
    "side bottom bottom";
  gap: var(--cc-gap, 16px);
  min-height: calc(100dvh - 170px);
  align-items: stretch;
}
.alog-side { grid-area: side; }
.alog-topbar { grid-area: topbar; }
.alog-center { grid-area: center; }
.alog-right { grid-area: rightcol; }
.alog-bottom { grid-area: bottom; }

/* ---- generic panel chrome ---- */
.alog-panel { padding: 16px 18px; display: flex; flex-direction: column; min-height: 0; }
.alog-panel__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-bottom: 12px;
}
.alog-eyebrow {
  margin: 0; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--cc-text-muted, #64748b);
}
.alog-title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--cc-text); }
.alog-link {
  font-size: 11px; font-weight: 600; color: var(--cc-accent, #3b82f6);
  text-decoration: none; letter-spacing: 0.02em;
}
.alog-link:hover { text-decoration: underline; }

/* ---- left sidebar ---- */
.alog-side { gap: 16px; }
.alog-side__brand { margin-bottom: 4px; }
.alog-tabs { display: flex; flex-direction: column; gap: 8px; }
.alog-tab {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 12px; border-radius: 12px; cursor: pointer;
  border: 1px solid transparent; background: rgba(148, 163, 184, 0.10);
  color: var(--cc-text); font-weight: 600; font-size: 13px;
  text-align: left; width: 100%; transition: background 0.15s, border-color 0.15s;
}
.alog-tab:hover { background: rgba(148, 163, 184, 0.18); }
.alog-tab__ico {
  width: 30px; height: 30px; flex: 0 0 auto; display: grid; place-items: center;
  border-radius: 9px; font-size: 14px;
  background: rgba(148, 163, 184, 0.18); color: var(--cc-text-muted, #64748b);
}
.alog-tab--on {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.20), rgba(99, 102, 241, 0.16));
  border-color: rgba(59, 130, 246, 0.45);
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.14);
}
.alog-tab--on .alog-tab__ico {
  background: linear-gradient(135deg, #0ea5e9, #6366f1); color: #fff;
}
.alog-filters { display: flex; flex-direction: column; gap: 12px; }
.alog-field { display: flex; flex-direction: column; gap: 5px; }
.alog-field > span {
  font-size: 0.64rem; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--cc-text-muted, #64748b);
}
.alog-field input,
.alog-field select {
  width: 100%; box-sizing: border-box; padding: 9px 11px;
  border-radius: 10px; border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(255, 255, 255, 0.42); color: var(--cc-text);
  font-size: 13px; outline: none;
}
.alog-field input:focus,
.alog-field select:focus { border-color: rgba(59, 130, 246, 0.55); }
.alog-apply { width: 100%; justify-content: center; margin-top: 2px; }

/* ---- top bar (range pills) ---- */
.alog-topbar {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 12px 16px;
}
.alog-range { display: flex; gap: 8px; flex-wrap: wrap; }
.alog-pill {
  padding: 8px 16px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: rgba(148, 163, 184, 0.10); color: var(--cc-text);
  font-size: 12.5px; font-weight: 600; transition: background 0.15s, border-color 0.15s;
}
.alog-pill:hover { background: rgba(148, 163, 184, 0.18); }
.alog-pill--on {
  background: linear-gradient(135deg, #2563eb, #4f46e5); color: #fff;
  border-color: transparent; box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
}
.alog-topbar__right { display: flex; align-items: center; gap: 10px; }

/* ---- center timeline ---- */
.alog-center { min-height: 0; }
.alog-feed-wrap {
  flex: 1 1 auto; min-height: 0; overflow: auto; padding-right: 4px;
  display: flex; flex-direction: column; gap: 10px;
}
.alog-item {
  display: grid; grid-template-columns: 64px 38px minmax(0, 1fr) auto;
  gap: 12px; align-items: start;
  padding: 12px 14px; border-radius: 14px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}
.alog-item__time { display: flex; flex-direction: column; gap: 2px; }
.alog-item__ts { font-size: 12px; font-weight: 700; color: var(--cc-text); font-variant-numeric: tabular-nums; }
.alog-item__rel { font-size: 10.5px; color: var(--cc-text-muted, #64748b); }
.alog-item__ico {
  width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center;
  font-size: 14px; color: #fff;
}
.alog-item__body { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.alog-item__title { font-size: 13.5px; font-weight: 600; color: var(--cc-text); line-height: 1.35; }
.alog-item__meta {
  display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center;
  font-size: 11.5px; color: var(--cc-text-muted, #64748b);
}
.alog-item__meta .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.alog-item__tags { display: flex; flex-direction: column; gap: 5px; align-items: flex-end; }
.alog-chip {
  display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 999px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.02em; white-space: nowrap;
  background: rgba(148, 163, 184, 0.16); color: var(--cc-text-muted, #475569);
}
.alog-chip--critical { background: rgba(239, 68, 68, 0.16); color: #b91c1c; }
.alog-chip--high { background: rgba(249, 115, 22, 0.16); color: #c2410c; }
.alog-chip--medium { background: rgba(234, 179, 8, 0.18); color: #a16207; }
.alog-chip--low { background: rgba(34, 197, 94, 0.16); color: #15803d; }
.alog-chip--info { background: rgba(59, 130, 246, 0.16); color: #1d4ed8; }
.alog-dot--critical { background: linear-gradient(135deg, #ef4444, #b91c1c); }
.alog-dot--high { background: linear-gradient(135deg, #f97316, #c2410c); }
.alog-dot--medium { background: linear-gradient(135deg, #eab308, #a16207); }
.alog-dot--low { background: linear-gradient(135deg, #22c55e, #15803d); }
.alog-dot--info { background: linear-gradient(135deg, #38bdf8, #2563eb); }
.alog-empty { color: var(--cc-text-muted, #64748b); padding: 28px 8px; text-align: center; }

/* ---- right analytics column ---- */
.alog-right { gap: var(--cc-gap, 16px); display: flex; flex-direction: column; }
.alog-right .alog-panel { flex: 0 0 auto; }
.alog-donut-wrap { display: flex; align-items: center; gap: 16px; }
.alog-donut {
  width: 116px; height: 116px; border-radius: 50%; flex: 0 0 auto; position: relative;
  background: conic-gradient(rgba(148,163,184,0.25) 0 100%);
}
.alog-donut::after {
  content: ""; position: absolute; inset: 22px; border-radius: 50%;
  background: var(--cc-card-bg, rgba(255,255,255,0.78));
  box-shadow: inset 0 1px 4px rgba(15, 23, 42, 0.10);
}
.alog-donut__center {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; z-index: 1;
}
.alog-donut__total { font-size: 1.15rem; font-weight: 800; color: var(--cc-text); line-height: 1; }
.alog-donut__label { font-size: 9.5px; color: var(--cc-text-muted, #64748b); letter-spacing: 0.06em; text-transform: uppercase; }
.alog-legend { display: flex; flex-direction: column; gap: 7px; flex: 1 1 auto; min-width: 0; }
.alog-legend__row { display: flex; align-items: center; gap: 8px; font-size: 12px; }
.alog-legend__sw { width: 10px; height: 10px; border-radius: 3px; flex: 0 0 auto; }
.alog-legend__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--cc-text); }
.alog-legend__pct { font-weight: 700; color: var(--cc-text-muted, #64748b); font-variant-numeric: tabular-nums; }

.alog-break { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; }
.alog-break__cell {
  border-radius: 12px; padding: 12px 8px; text-align: center;
  background: rgba(148, 163, 184, 0.10);
}
.alog-break__v { font-size: 1.2rem; font-weight: 800; line-height: 1; }
.alog-break__k { font-size: 9.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; margin-top: 5px; color: var(--cc-text-muted, #64748b); }

.alog-rank { display: flex; flex-direction: column; gap: 9px; }
.alog-rank__row { display: flex; align-items: center; gap: 10px; font-size: 12.5px; }
.alog-rank__n {
  width: 22px; height: 22px; flex: 0 0 auto; border-radius: 7px; display: grid; place-items: center;
  font-size: 11px; font-weight: 700; background: rgba(148, 163, 184, 0.18); color: var(--cc-text-muted, #475569);
}
.alog-rank__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--cc-text); }
.alog-rank__c { font-weight: 700; color: var(--cc-text-muted, #64748b); font-variant-numeric: tabular-nums; }

.alog-insight { border: 1px solid rgba(99, 102, 241, 0.35); }
.alog-insight__title { font-weight: 700; font-size: 13px; margin: 0 0 6px; color: var(--cc-text); }
.alog-insight__body { font-size: 12px; line-height: 1.5; color: var(--cc-text-muted, #475569); margin: 0; }

/* ---- bottom strip ---- */
.alog-bottom {
  display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.3fr) minmax(0, 0.9fr);
  gap: var(--cc-gap, 16px);
}
.alog-bottom .alog-panel { min-height: 0; }
.alog-chart-canvas { width: 100%; height: 120px; display: block; }
.alog-heatmap { display: flex; flex-direction: column; gap: 4px; }
.alog-heatmap__row { display: grid; grid-template-columns: 30px repeat(24, 1fr); gap: 3px; align-items: center; }
.alog-heatmap__day { font-size: 9.5px; color: var(--cc-text-muted, #64748b); }
.alog-heatmap__cell { aspect-ratio: 1 / 1; border-radius: 3px; background: rgba(148, 163, 184, 0.12); }
.alog-export { display: flex; flex-direction: column; gap: 10px; }
.alog-export__hint { font-size: 12px; color: var(--cc-text-muted, #64748b); margin: 0 0 4px; }

@media (max-width: 1180px) {
  .alog-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto auto;
    grid-template-areas:
      "side"
      "topbar"
      "center"
      "rightcol"
      "bottom";
    min-height: 0;
  }
  .alog-bottom { grid-template-columns: 1fr; }
  .alog-feed-wrap { max-height: 60vh; }
}
`;

/** @returns {boolean} */
export function ensureAuditLogStyles() {
  if (typeof document === "undefined") return false;
  if (document.getElementById(AUDIT_LOG_STYLE_ID)) return true;
  const el = document.createElement("style");
  el.id = AUDIT_LOG_STYLE_ID;
  el.textContent = AUDIT_LOG_STYLE_TEXT;
  document.head.appendChild(el);
  return true;
}

export function removeAuditLogStyles() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(AUDIT_LOG_STYLE_ID);
  if (el) el.remove();
}
